/**
 * ProfileView Component (T082-T093)
 *
 * Displays AI-generated psychological profiles with loading state and polling.
 */

import React, { useState, useEffect } from 'react'
import { questionnaireService } from '@/services/questionnaireService'
import styles from './ProfileView.module.css'

interface Profile {
  summary: string
  emotional_patterns?: string[]
  key_concerns?: string[]
  coping_mechanisms?: string[]
  support_needs?: string[]
  confidence_score?: number
  category: string
  generated_at: string
  model_version?: string
}

interface ProfileViewProps {
  responseId: number
  onClose: () => void
}

const CATEGORY_LABELS: Record<string, string> = {
  'marriage_relationship': 'Marriage & Relationship',
  'stress_anxiety': 'Stress & Anxiety',
  'not_defined': 'Not Defined / General'
}

export const ProfileView: React.FC<ProfileViewProps> = ({ responseId, onClose }) => {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pollingAttempts, _setPollingAttempts] = useState(0)

  // T083: Implement polling logic
  useEffect(() => {
    fetchProfile()
  }, [responseId])

  const fetchProfile = async () => {
    setLoading(true)
    setError(null)

    try {
      // T083: Poll for profile with timeout
      const profileData = await questionnaireService.pollForProfile(responseId, 15, 2000)

      if (profileData.status === 'processing') {
        // Still processing after max attempts
        setError('Profile generation is taking longer than expected. Please check back in a moment.')
        setLoading(false)
      } else {
        // Profile ready
        setProfile(profileData as Profile)
        setLoading(false)
      }
    } catch (err: any) {
      console.error('Error fetching profile:', err)
      setError(err.message || 'Failed to load profile')
      setLoading(false)
    }
  }

  // T084: Display loading state
  if (loading) {
    return (
      <div className={styles.profileContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <h3 className={styles.loadingTitle}>Generating Your Profile...</h3>
          <p className={styles.loadingMessage}>
            Our AI is analyzing your responses to create a personalized psychological profile.
            This usually takes 5-10 seconds.
          </p>
          {pollingAttempts > 5 && (
            <p className={styles.loadingSubtext}>
              Still working on it... ({pollingAttempts} attempts)
            </p>
          )}
        </div>
      </div>
    )
  }

  // T091: Error handling
  if (error) {
    return (
      <div className={styles.profileContainer}>
        <div className={styles.errorContainer} role="alert">
          <h3 className={styles.errorTitle}>Unable to Load Profile</h3>
          <p className={styles.errorMessage}>{error}</p>
          <div className={styles.errorActions}>
            <button
              className={styles.retryButton}
              onClick={fetchProfile}
            >
              Try Again
            </button>
            <button
              className={styles.closeButton}
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Profile loaded successfully
  if (!profile) {
    return null
  }

  const categoryLabel = CATEGORY_LABELS[profile.category] || profile.category
  const confidencePercentage = profile.confidence_score
    ? Math.round(profile.confidence_score * 100)
    : null

  return (
    <div className={styles.profileContainer} role="article" aria-label="Psychological profile">
      <div className={styles.header}>
        <h2 className={styles.title}>Your Psychological Profile</h2>
        <p className={styles.subtitle}>
          Based on your {categoryLabel} assessment
        </p>
        {confidencePercentage && (
          <div className={styles.confidenceBadge}>
            {confidencePercentage}% confidence
          </div>
        )}
      </div>

      {/* T085: Render profile summary */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Summary</h3>
        <p className={styles.summaryText}>{profile.summary}</p>
      </div>

      {/* T086: Render emotional patterns */}
      {profile.emotional_patterns && profile.emotional_patterns.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Emotional Patterns</h3>
          <ul className={styles.list}>
            {profile.emotional_patterns.map((pattern, index) => (
              <li key={index} className={styles.listItem}>
                {pattern}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* T087: Render key concerns */}
      {profile.key_concerns && profile.key_concerns.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Key Concerns</h3>
          <ul className={styles.list}>
            {profile.key_concerns.map((concern, index) => (
              <li key={index} className={styles.listItem}>
                {concern}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* T088: Render coping mechanisms */}
      {profile.coping_mechanisms && profile.coping_mechanisms.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Coping Mechanisms</h3>
          <ul className={styles.list}>
            {profile.coping_mechanisms.map((mechanism, index) => (
              <li key={index} className={styles.listItem}>
                {mechanism}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* T089: Render support needs */}
      {profile.support_needs && profile.support_needs.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Recommended Support</h3>
          <ul className={styles.list}>
            {profile.support_needs.map((need, index) => (
              <li key={index} className={styles.listItem}>
                {need}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Success message */}
      <div className={styles.successMessage}>
        <p>Profile created! Your responses help us personalize support.</p>
      </div>

      <div className={styles.actions}>
        <button
          className={styles.doneButton}
          onClick={onClose}
          aria-label="Close profile view"
        >
          Done
        </button>
      </div>

      {/* T093: Metadata footer */}
      <div className={styles.metadata}>
        <p className={styles.metadataText}>
          Generated on {new Date(profile.generated_at).toLocaleString()}
        </p>
        {profile.model_version && (
          <p className={styles.metadataText}>
            Model: {profile.model_version}
          </p>
        )}
      </div>
    </div>
  )
}
